/**
 * NotificationsBell — bell icon with badge + click-through to /notifications.
 *
 * Lightweight: polls the summary endpoint every 30s while the tab is
 * visible, pauses when hidden. The actual panel UI lives at
 * /notifications (single dedicated page). The bell is just a teaser.
 *
 * Founder spec (Section 4): real-time acceptance reports. We use polling
 * (not WebSockets/SSE) because the data is low-velocity and polling is
 * resilient to mobile reconnect. Switching to SSE later is a one-file
 * upgrade behind this same UI.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const POLL_MS = 30000;

export default function NotificationsBell() {
  const { user } = useAuth();
  const isAuth = !!user && user !== false;
  const [pending, setPending] = useState(0);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!isAuth) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      try {
        const { data } = await api.get("/notifications/summary");
        if (cancelled) return;
        setPending(data?.pending || 0);
        setUnread(data?.unread || 0);
      } catch { /* offline / unauthorized — skip */ }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    const onRefresh = () => { tick(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("sonspeak:notifications-refresh", onRefresh);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("sonspeak:notifications-refresh", onRefresh);
    };
  }, [isAuth]);

  if (!isAuth) return null;

  const showDot = pending > 0 || unread > 0;
  return (
    <Link
      to="/notifications"
      aria-label={pending > 0 ? `${pending} acceptance reports waiting` : "Notifications"}
      title={pending > 0 ? `${pending} waiting for your approval` : "Notifications"}
      className="relative inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-[color:var(--paper-muted)] transition"
      data-testid="notifications-bell"
    >
      <Bell size={18} aria-hidden />
      {showDot && (
        <span
          className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-extrabold text-white rounded-full px-1 ${
            pending > 0 ? "bg-[color:var(--coral)]" : "bg-[color:var(--ink)]"
          }`}
          data-testid="notifications-bell-badge"
        >
          {(pending || unread) > 9 ? "9+" : pending || unread}
        </span>
      )}
    </Link>
  );
}
